import { useEffect, useState } from 'react';

import type { useLatestOrderAnalytics } from '@src/hooks/useLatestOrderAnalytics';
import {
  Box,
  Button,
  ButtonGroup,
  ButtonVariants,
  Card,
  EmptyView,
  Flex,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  IndicatorBadge,
  LoaderTalos,
  SymbolDisplay,
  SymbolDisplaySize,
  UnifiedLiquidityEnum,
  getSymbolsAcrossMarkets,
  useCallbackRef,
  type IOrderConfigurationSnapshot,
  type Order,
} from '@talos/kyoko';
import type { UseQueryResult } from '@tanstack/react-query';
import { BehaviorSubject, type Subject } from 'rxjs';
import { useTheme } from 'styled-components';
import { TrajectoryZoomMode, defaultTrajectoryZoom, type OrderTrajectoryZoom } from './chart';
import { OrderTrajectoryChart } from './OrderTrajectoryChart';
import { useConfigSnapshotQuery } from './queries';
import { isClipBased } from './util';

export interface OrderTrajectoryChartsProps {
  order: Order;
  isMultileg: boolean;
  strategy?: string;
  height: number;
  width?: number;
  // If specified, reflow will resize the graph
  resizeAware?: boolean;
  analytics: ReturnType<typeof useLatestOrderAnalytics>;
}

export const OrderTrajectoryCharts = (props: OrderTrajectoryChartsProps) => {
  const { isMultileg, analytics, order } = props;
  const [zoomSubject] = useState<BehaviorSubject<OrderTrajectoryZoom>>(
    () => new BehaviorSubject<OrderTrajectoryZoom>(defaultTrajectoryZoom)
  );
  const underlyingSymbols =
    order.unifiedLiquidity === UnifiedLiquidityEnum.Enabled ? getSymbolsAcrossMarkets(order) : [];

  const configSnapshotQuery = useConfigSnapshotQuery(order.OrderID);

  const handleZoomChange = useCallbackRef((zoom: Partial<OrderTrajectoryZoom>) => {
    const current = zoomSubject.getValue();
    zoomSubject.next({
      ...current,
      ...zoom,
    });
  });

  return (
    <>
      {isMultileg ? (
        [1, 2].map(legIndex => (
          <Card
            key={legIndex}
            data-testid={`order-trajectory-chart-${legIndex}`}
            title={
              <HStack justifyContent="flex-start" color="colorTextImportant">
                <IndicatorBadge h={18} mr="spacing8">
                  <HStack gap="spacing2">
                    <Icon icon={IconName.ChartLine} /> {legIndex}
                  </HStack>
                </IndicatorBadge>
                <SymbolDisplay
                  symbol={analytics?.latestAnalytics?.[legIndex]?.Symbol}
                  showDescription={false}
                  p="0"
                  mr="spacing10"
                  underlyingSymbols={legIndex === 0 ? underlyingSymbols : undefined}
                  size={SymbolDisplaySize.Small}
                />
                <TrajectoryZoomButtons order={order} zoomSubject={zoomSubject} onZoomChange={handleZoomChange} />
              </HStack>
            }
          >
            <ChartForLeg
              legIndex={legIndex}
              configSnapshotQuery={configSnapshotQuery}
              zoomSubject={zoomSubject}
              onZoomChange={handleZoomChange}
              {...props}
            />
          </Card>
        ))
      ) : (
        <Card
          title={
            <HStack justifyContent="space-between" color="colorTextImportant" gap="spacing10">
              <span>Order Trajectory</span>
              <TrajectoryZoomButtons order={order} zoomSubject={zoomSubject} onZoomChange={handleZoomChange} />
            </HStack>
          }
          data-testid="order-trajectory-chart"
        >
          <ChartForLeg
            legIndex={1}
            configSnapshotQuery={configSnapshotQuery}
            zoomSubject={zoomSubject}
            onZoomChange={handleZoomChange}
            {...props}
          />
        </Card>
      )}
    </>
  );
};

const TrajectoryZoomButtons = ({
  order,
  zoomSubject,
  onZoomChange,
}: {
  order: Order;
  zoomSubject: Subject<OrderTrajectoryZoom>;
  onZoomChange: (zoom: Partial<OrderTrajectoryZoom>) => void;
}) => {
  const [mode, setMode] = useState<TrajectoryZoomMode>(TrajectoryZoomMode.None);

  useEffect(() => {
    const sub = zoomSubject?.subscribe(zoom => {
      setMode(zoom.mode);
    });
    return () => sub?.unsubscribe();
  }, [zoomSubject]);

  const getVariant = (activeMode: TrajectoryZoomMode) =>
    mode === activeMode ? ButtonVariants.Primary : ButtonVariants.Default;

  return (
    <ButtonGroup size={FormControlSizes.Small}>
      <Button
        variant={getVariant(TrajectoryZoomMode.ZoomFit)}
        onClick={() =>
          onZoomChange({
            mode: TrajectoryZoomMode.ZoomFit,
          })
        }
      >
        Zoom Fit
      </Button>
      {isClipBased(order) && (
        <Button
          variant={getVariant(TrajectoryZoomMode.ZoomClip)}
          onClick={() =>
            onZoomChange({
              mode: TrajectoryZoomMode.ZoomClip,
            })
          }
        >
          Zoom Clip
        </Button>
      )}
    </ButtonGroup>
  );
};

const LEGEND_HEIGHT = 34;
const ChartForLeg = ({
  legIndex,
  analytics,
  configSnapshotQuery,
  zoomSubject,
  onZoomChange,
  ...props
}: {
  legIndex: number;
  configSnapshotQuery: UseQueryResult<IOrderConfigurationSnapshot, Error>;
  zoomSubject: BehaviorSubject<OrderTrajectoryZoom>;
  onZoomChange: (zoom: Partial<OrderTrajectoryZoom>) => void;
} & OrderTrajectoryChartsProps) => {
  const currentLatestAnalytics = analytics?.latestAnalytics?.[legIndex];
  const currentlegOrParentAnalyticsObservable = analytics?.legOrParentAnalyticsObservable;
  const { spacing16 } = useTheme();

  const { data: configSnapshot, error: configSnapshotError } = configSnapshotQuery;

  return (
    // chart height + legends + margin
    <Box minHeight={`${props.height + LEGEND_HEIGHT + spacing16}px`} position="relative">
      {!currentLatestAnalytics || !currentlegOrParentAnalyticsObservable || !configSnapshot ? (
        <Flex justifyContent="center" position="absolute" inset="0">
          {configSnapshotError ? (
            <EmptyView>Order config snapshot not available</EmptyView>
          ) : analytics.receivedAnalytics ? (
            <EmptyView>Order Trajectory data not available</EmptyView>
          ) : (
            <LoaderTalos />
          )}
        </Flex>
      ) : (
        <OrderTrajectoryChart
          {...props}
          zoomSubject={zoomSubject}
          onZoomChange={onZoomChange}
          latestAnalytics={currentLatestAnalytics}
          legOrParentAnalyticsObservable={currentlegOrParentAnalyticsObservable}
          requestedResolution={analytics.requestedResolution}
          legIndex={legIndex}
          configSnapshot={configSnapshot}
        />
      )}
    </Box>
  );
};
